// Single source of truth for "which permission opens which part of the app".
// Used by the sidebar menu and by the role editor in the admin panel.

export type AccessLevel = 'none' | 'read' | 'edit' | 'full';

export interface PermissionModule {
    resource: string;
    label: string;
    tabs: string[];
    description: string;
}

export interface PermissionModuleGroup {
    title: string;
    modules: PermissionModule[];
}

export const PERMISSION_MODULE_GROUPS: PermissionModuleGroup[] = [
    {
        title: 'Sprzedaż',
        modules: [
            { resource: 'orders', label: 'Zamówienia', tabs: ['Dashboard', 'Zamówienia'], description: 'Lista zamówień, kanały sprzedaży i dashboard z wynikami.' },
            { resource: 'customers', label: 'Opinie', tabs: ['Opinie'], description: 'Kontakty z klientami w sprawie opinii i historia kontaktu.' },
            { resource: 'partners', label: 'Kontrahenci B2B', tabs: ['Kontrahenci B2B'], description: 'Kartoteka kontrahentów, cenniki, kontakty i komentarze.' },
            { resource: 'forecasts', label: 'Planowanie sprzedaży', tabs: ['Planowanie Sprzedaży'], description: 'Plany sprzedaży, założenia i realizacja planu.' },
        ],
    },
    {
        title: 'Produkty i magazyn',
        modules: [
            { resource: 'products', label: 'Produkty', tabs: ['Produkty'], description: 'Katalog produktów i ich parametry.' },
            { resource: 'inventory', label: 'Magazyn', tabs: ['Magazyn'], description: 'Stany magazynowe, partie i ruchy magazynowe.' },
        ],
    },
    {
        title: 'Finanse',
        modules: [
            { resource: 'costs', label: 'Koszty', tabs: ['Koszty'], description: 'Rejestr kosztów, dostawcy i budżet kosztowy.' },
            { resource: 'budgets', label: 'Finanse', tabs: ['Finanse'], description: 'Przychody i koszty (Financial Hub), płatności i wpisy cykliczne.' },
        ],
    },
    {
        title: 'Organizacja',
        modules: [
            { resource: 'tasks', label: 'Zadania', tabs: ['Zadania', 'Zadania Trello'], description: 'Tablice zadań i link do Trello.' },
            { resource: 'strategy', label: 'Strategia', tabs: ['Strategia'], description: 'Cele i inicjatywy strategiczne.' },
            { resource: 'integrations', label: 'Integracje', tabs: ['Integracje'], description: 'Połączenia z Allegro, sklepami i innymi systemami.' },
        ],
    },
    {
        title: 'Raportowanie',
        modules: [
            { resource: 'bdo', label: 'BDO', tabs: ['BDO'], description: 'Raport opakowań BDO: tabela 5.1, sprzedaż, opakowania zbiorcze i braki.' },
        ],
    },
    {
        title: 'Administracja',
        modules: [
            { resource: 'users', label: 'Użytkownicy', tabs: ['Administracja → Użytkownicy'], description: 'Zakładanie kont i przypisywanie ról. „Pełny” pozwala tworzyć konta i nadawać role.' },
            { resource: 'roles', label: 'Role i uprawnienia', tabs: ['Administracja → Role'], description: 'Ten ekran. „Pełny” pozwala zmieniać uprawnienia ról.' },
            { resource: 'dictionaries', label: 'Słowniki', tabs: ['Administracja → Słowniki'], description: 'Listy wartości używane w formularzach.' },
            { resource: 'settings', label: 'Ustawienia systemu', tabs: ['Administracja → Reguły, Moduły, Ważne'], description: 'Reguły biznesowe i włączanie modułów.' },
            { resource: 'feedback', label: 'Zgłoszenia błędów', tabs: ['Administracja → Zgłoszenia'], description: 'Przeglądanie i obsługa zgłoszeń od użytkowników.' },
        ],
    },
];

export const ACCESS_LEVELS: { value: AccessLevel; label: string; hint: string; actions: string[] }[] = [
    { value: 'none', label: 'Brak', hint: 'Zakładka ukryta', actions: [] },
    { value: 'read', label: 'Podgląd', hint: 'Tylko przeglądanie', actions: ['read'] },
    { value: 'edit', label: 'Edycja', hint: 'Przeglądanie, dodawanie i edycja', actions: ['read', 'create', 'update'] },
    { value: 'full', label: 'Pełny', hint: 'Wszystko, łącznie z usuwaniem', actions: ['read', 'create', 'update', 'delete', 'manage'] },
];

export const MANAGED_RESOURCES = new Set(
    PERMISSION_MODULE_GROUPS.flatMap((g) => g.modules.map((m) => m.resource)),
);

/** Derives the access level from the actions a role has on a resource. */
export function levelFromActions(actions: string[]): AccessLevel {
    const has = (a: string) => actions.includes(a);
    if (has('manage') || has('delete')) return 'full';
    if (has('create') || has('update')) return 'edit';
    if (has('read')) return 'read';
    return 'none';
}
